import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  IconButton,
  Rating,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";

interface QuestionnaireModalProps {
  open: boolean;
  onClose: () => void;
  memberId: string;
  questionnaireId: number;
  responseId: string | null;
  activityId?: number | null;
  onQuestionnaireChange?: (
    isComplete: boolean,
    answers: { [key: number]: string },
    responseId?: string | null
  ) => void;
  onSubmit?: (answers: { [key: number]: string }) => void;
  isActivityContext?: boolean;
  refreshMembers: () => void;
  currentAnswers?: { [key: number]: string };
  isViewOnly?: boolean;
  loadAnswers?: boolean;
}

interface Question {
  id: number;
  text: string;
  question_type: "Yes/No" | "Multiple Choice" | "Likert Scale" | "Text";
  options?: string;
}

const QuestionnaireModal: React.FC<QuestionnaireModalProps> = ({
  open,
  onClose,
  memberId,
  questionnaireId,
  responseId,
  activityId,
  onQuestionnaireChange,
  onSubmit,
  isActivityContext,
  refreshMembers,
  currentAnswers,
  isViewOnly = false,
  loadAnswers,
}) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<{ [key: number]: string }>({});
  const [isComplete, setIsComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [shouldFetchQuestions, setShouldFetchQuestions] = useState(true);

  useEffect(() => {
    if (open && questionnaireId) {
      shouldFetchQuestions && fetchQuestions();

      if (responseId) {
        fetchResponse(responseId);
      } else if (currentAnswers && Object.keys(currentAnswers).length > 0) {
        setAnswers(currentAnswers);
      } else {
        setAnswers({});
      }
    }
  }, [open, questionnaireId, responseId, currentAnswers]);

  useEffect(() => {
    const complete =
      questions.length > 0 &&
      questions.every((q) => answers[q.id] && answers[q.id].trim() !== "");
    setIsComplete(complete);

    if (onQuestionnaireChange && !isViewOnly) {
      onQuestionnaireChange(complete, answers, responseId);
    }
  }, [answers, questions, onQuestionnaireChange, responseId, isViewOnly]);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/questionnaires/${questionnaireId}/questions`
      );
      setQuestions(response.data);
    } catch (error) {
      console.error("Error fetching questions:", error);
    } finally {
      setLoading(false);
      setShouldFetchQuestions(false);
    }
  };

  const fetchResponse = async (respId: string) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/members/${memberId}/questionnaires/${questionnaireId}/responses/${respId}`
      );
      const responseAnswers = response.data.reduce(
        (
          acc: { [key: number]: string },
          curr: { id: number; answer: string }
        ) => {
          acc[curr.id] = curr.answer;
          return acc;
        },
        {}
      );
      setAnswers(responseAnswers);
      setIsComplete(true);
    } catch (error) {
      console.error("Error fetching response:", error);
    }
  };

  const handleAnswerChange = (questionId: number, value: string) => {
    if (isViewOnly) return;
    const newAnswers = { ...answers, [questionId]: value };
    setAnswers(newAnswers);
    const allAnswered = questions.every(
      (q) => newAnswers[q.id] && newAnswers[q.id].trim() !== ""
    );
    setIsComplete(allAnswered);
    if (onQuestionnaireChange) {
      onQuestionnaireChange(allAnswered, newAnswers, responseId);
    }
  };

  const handleClear = () => {
    if (isViewOnly) return;
    setAnswers({});
    setIsComplete(false);
    if (onQuestionnaireChange) {
      onQuestionnaireChange(false, {}, responseId);
    }
  };

  const handleSubmit = async () => {
    if (isViewOnly) return;
    if (isComplete) {
      if (isActivityContext) {
        if (onQuestionnaireChange) {
          onQuestionnaireChange(true, answers, responseId);
        }
      } else {
        try {
          setLoading(true);
          const response = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/members/${memberId}/questionnaires/${questionnaireId}/submit`,
            { answers, responseId }
          );
          if (onQuestionnaireChange) {
            onQuestionnaireChange(true, answers, response.data.responseId);
          }
          refreshMembers();
        } catch (error) {
          console.error("Error submitting questionnaire:", error);
        } finally {
          setLoading(false);
        }
      }
      onClose();
    }
  };

  const renderQuestion = (question: Question) => {
    switch (question.question_type) {
      case "Yes/No":
        return (
          <FormControl component="fieldset">
            <RadioGroup
              value={answers[question.id] || ""}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            >
              <FormControlLabel
                value="Yes"
                control={<Radio disabled={isViewOnly} />}
                label="Yes"
              />
              <FormControlLabel
                value="No"
                control={<Radio disabled={isViewOnly} />}
                label="No"
              />
            </RadioGroup>
          </FormControl>
        );
      case "Multiple Choice":
        if (question.options) {
          const options = JSON.parse(question.options);
          return (
            <FormControl component="fieldset">
              <RadioGroup
                value={answers[question.id] || ""}
                onChange={(e) =>
                  handleAnswerChange(question.id, e.target.value)
                }
              >
                {options.map((option: string) => (
                  <FormControlLabel
                    key={option}
                    value={option}
                    control={<Radio disabled={isViewOnly} />}
                    label={option}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          );
        }
        return null;
      case "Likert Scale":
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Rating
              name={`question-${question.id}`}
              value={Number(answers[question.id]) || 0}
              onChange={(event, newValue) => {
                handleAnswerChange(question.id, newValue?.toString() || "");
              }}
              max={5}
              readOnly={isViewOnly}
            />
            <Box
              sx={{
                mt: 1,
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Typography variant="caption">Strongly Disagree</Typography>
              <Typography variant="caption">Strongly Agree</Typography>
            </Box>
          </Box>
        );
      case "Text":
        return (
          <TextField
            fullWidth
            multiline
            rows={3}
            value={answers[question.id] || ""}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            disabled={isViewOnly}
          />
        );
      default:
        return null;
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 600,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6" component="h2">
            {isViewOnly
              ? "View Questionnaire Responses"
              : responseId
              ? "Edit Questionnaire Responses"
              : "Fill Questionnaire"}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {questions.map((question) => (
          <Box key={question.id} sx={{ mb: 2 }}>
            <Typography>{question.text}</Typography>
            {renderQuestion(question)}
          </Box>
        ))}
        <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between" }}>
          {!isViewOnly && (
            <>
              <Button onClick={handleClear} disabled={!!responseId}>
                Clear
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!isComplete}
              >
                {isActivityContext ? "Save" : "Submit"}
              </Button>
            </>
          )}
          {isViewOnly && (
            <Button variant="contained" onClick={onClose}>
              Close
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default QuestionnaireModal;
